import { useEffect, useRef, useState, useMemo } from 'react';
import * as echarts from 'echarts';
import { Trophy, BarChart3, Radar, Users } from 'lucide-react';
import { PlayerRole, ROLE_NAMES, ROLE_FACTION } from '../types/stats';
import { GameHistoryItem, PlayerRoundRecord } from '../modules/stats/types';

interface RoleStats {
  role: PlayerRole;
  roleName: string;
  faction: 'good' | 'evil';
  games: number;
  wins: number;
  winRate: number;
  avgScore: number;
  pickRate: number;
}

const STANDARD_ROLES: PlayerRole[] = ['merlin', 'percival', 'loyal', 'mordred', 'morgana', 'assassin', 'minion', 'oberon'];

interface RoleStatsChartProps {
  playerStats: {
    player_name?: string;
    total_games?: number;
    percival_games: number;
    percival_wins: number;
    merlin_games: number;
    merlin_wins: number;
    assassin_games: number;
    assassin_wins: number;
    morgana_games?: number;
    morgana_wins?: number;
    mordred_games?: number;
    mordred_wins?: number;
    minion_games?: number;
    minion_wins?: number;
    loyal_games?: number;
    loyal_wins?: number;
    lancelot_good_games?: number;
    lancelot_good_wins?: number;
    lancelot_evil_games?: number;
    lancelot_evil_wins?: number;
    oberon_games?: number;
    oberon_wins?: number;
  };
  games?: GameHistoryItem[];
}

export default function RoleStatsChart({ playerStats, games }: RoleStatsChartProps) {
  const pickRateChartRef = useRef<HTMLDivElement>(null);
  const winRateChartRef = useRef<HTMLDivElement>(null);
  const pickRateChartInstance = useRef<echarts.ECharts | null>(null);
  const winRateChartInstance = useRef<echarts.ECharts | null>(null);
  const [activeView, setActiveView] = useState<'pick' | 'win'>('pick');
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // V186: 计算角色统计
  const roleStats: RoleStats[] = useMemo(() => {
    const targetPlayerName = playerStats.player_name;
    const totalGames = playerStats.total_games || 0;
    
    if (games && games.length > 0 && targetPlayerName) {
      const playerRecords: PlayerRoundRecord[] = [];
      games.forEach(game => {
        const player = game.players?.find(p => p.player_nickname === targetPlayerName);
        if (player) playerRecords.push(player);
      });
      
      const roleStatsMap: Record<string, { games: number; wins: number }> = {};
      STANDARD_ROLES.forEach(role => {
        roleStatsMap[role] = { games: 0, wins: 0 };
      });
      
      playerRecords.forEach(record => {
        const role = record.player_role;
        if (role && roleStatsMap[role]) {
          roleStatsMap[role].games++;
          if ((record.score_change || 0) > 0) {
            roleStatsMap[role].wins++;
          }
        }
      });
      
      const computedTotalGames = playerRecords.length;
      return STANDARD_ROLES.map(role => {
        const roleGames = roleStatsMap[role].games;
        return {
          role,
          roleName: ROLE_NAMES[role],
          faction: ROLE_FACTION[role],
          games: roleGames,
          wins: roleStatsMap[role].wins,
          winRate: roleGames > 0 ? Math.round((roleStatsMap[role].wins / roleGames) * 100) : 0,
          avgScore: 0,
          pickRate: computedTotalGames > 0 ? Math.round((roleGames / computedTotalGames) * 100) : 0,
        };
      });
    }
    
    const stats = STANDARD_ROLES.map(role => {
      const roleKey = role as string;
      const roleGames = (playerStats[`${roleKey}_games` as keyof typeof playerStats] as number) || 0;
      const wins = (playerStats[`${roleKey}_wins` as keyof typeof playerStats] as number) || 0;
      
      return {
        role,
        roleName: ROLE_NAMES[role],
        faction: ROLE_FACTION[role],
        games: roleGames,
        wins,
        winRate: roleGames > 0 ? Math.round((wins / roleGames) * 100) : 0,
        avgScore: 0,
        pickRate: totalGames > 0 ? Math.round((roleGames / totalGames) * 100) : 0,
      };
    });
    
    return stats;
  }, [playerStats, games]);

  // V186: 计算阵营抓取率
  const factionPickRates = useMemo(() => {
    const totalGames = playerStats.total_games || 0;
    if (totalGames === 0) return { good: 0, evil: 0 };
    
    const goodGames = (playerStats.percival_games || 0) + 
                     (playerStats.merlin_games || 0) + 
                     (playerStats.loyal_games || 0) + 
                     (playerStats.lancelot_good_games || 0);
    const evilGames = (playerStats.mordred_games || 0) + 
                     (playerStats.morgana_games || 0) + 
                     (playerStats.assassin_games || 0) + 
                     (playerStats.minion_games || 0) + 
                     (playerStats.oberon_games || 0) +
                     (playerStats.lancelot_evil_games || 0);
    
    return {
      good: Math.round((goodGames / totalGames) * 100),
      evil: Math.round((evilGames / totalGames) * 100),
    };
  }, [playerStats]);

  // V186: 初始化角色抓取率柱状图
  useEffect(() => {
    if (!pickRateChartRef.current || roleStats.length === 0) return;

    if (pickRateChartInstance.current) {
      pickRateChartInstance.current.dispose();
      pickRateChartInstance.current = null;
    }

    const chart = echarts.init(pickRateChartRef.current);

    const option = {
      backgroundColor: 'transparent',
      title: {
        text: '游戏角色抓取率',
        left: 'center',
        top: 5,
        textStyle: {
          color: '#fff',
          fontSize: 14,
          fontWeight: 'bold',
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(30, 30, 50, 0.95)',
        borderColor: '#8b5cf6',
        borderWidth: 1,
        textStyle: { color: '#fff' },
        formatter: (params: any) => {
          const data = params[0];
          const roleName = data.name;
          const roleStat = roleStats.find(r => r.roleName === roleName);
          if (!roleStat) return '';
          return `
            <div style="padding: 8px;">
              <div style="font-weight: bold; margin-bottom: 4px; color: ${roleStat.faction === 'good' ? '#60a5fa' : '#f87171'};">
                ${roleName}
              </div>
              <div>抓取率: ${roleStat.pickRate}%</div>
              <div>出场: ${roleStat.games} 次</div>
              <div>胜场: ${roleStat.wins} 次</div>
              <div>胜率: ${roleStat.winRate}%</div>
            </div>
          `;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '18%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: roleStats.map(r => r.roleName),
        axisLabel: {
          color: '#9ca3af',
          interval: 0,
          rotate: 30,
        },
        axisLine: { lineStyle: { color: '#374151' } },
      },
      yAxis: {
        type: 'value',
        name: '抓取率(%)',
        nameTextStyle: {
          color: '#9ca3af',
          fontSize: 11,
        },
        minInterval: 1,
        max: 100,
        axisLabel: { 
          color: '#9ca3af',
          formatter: (value: number) => Math.round(value).toString(),
        },
        axisLine: { lineStyle: { color: '#374151' } },
        splitLine: { lineStyle: { color: '#374151', type: 'dashed' } },
      },
      series: [
        {
          name: '抓取率',
          type: 'bar',
          data: roleStats.map(r => ({
            value: r.pickRate,
            itemStyle: {
              color: r.faction === 'good' ? 'rgba(96, 165, 250, 0.8)' : 'rgba(248, 113, 113, 0.8)',
              borderColor: r.faction === 'good' ? '#60a5fa' : '#f87171',
              borderWidth: 1,
            },
          })),
          barWidth: '50%',
          label: {
            show: true,
            position: 'top',
            formatter: '{c}%',
            color: '#fff',
            fontSize: 11,
          },
        },
      ],
    };

    pickRateChartInstance.current = chart;
    chart.setOption(option as any);

    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        if (pickRateChartInstance.current) {
          pickRateChartInstance.current.resize();
        }
      }, 100);
    };
    window.addEventListener('resize', handleResize);
    
    // V187: 初始渲染后强制resize确保正确尺寸
    setTimeout(() => {
      if (pickRateChartInstance.current) {
        pickRateChartInstance.current.resize();
      }
    }, 50);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (pickRateChartInstance.current) {
        pickRateChartInstance.current.dispose();
        pickRateChartInstance.current = null;
      }
    };
  }, [roleStats]);

  // V186: 初始化角色胜率柱状图（替代原来的雷达图）
  useEffect(() => {
    if (!winRateChartRef.current || roleStats.length === 0) return;

    if (winRateChartInstance.current) {
      winRateChartInstance.current.dispose();
      winRateChartInstance.current = null;
    }

    const chart = echarts.init(winRateChartRef.current);

    const option = {
      backgroundColor: 'transparent',
      title: {
        text: '游戏角色胜率',
        left: 'center',
        top: 5,
        textStyle: {
          color: '#fff',
          fontSize: 14,
          fontWeight: 'bold',
        },
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: 'rgba(30, 30, 50, 0.95)',
        borderColor: '#8b5cf6',
        borderWidth: 1,
        textStyle: { color: '#fff' },
        formatter: (params: any) => {
          const data = params[0];
          const roleName = data.name;
          const roleStat = roleStats.find(r => r.roleName === roleName);
          if (!roleStat) return '';
          return `
            <div style="padding: 8px;">
              <div style="font-weight: bold; margin-bottom: 4px; color: ${roleStat.faction === 'good' ? '#60a5fa' : '#f87171'};">
                ${roleName}
              </div>
              <div>胜率: ${roleStat.winRate}%</div>
              <div>出场: ${roleStat.games} 次</div>
              <div>胜场: ${roleStat.wins} 次</div>
              <div>抓取率: ${roleStat.pickRate}%</div>
            </div>
          `;
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '18%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: roleStats.map(r => r.roleName),
        axisLabel: {
          color: '#9ca3af',
          interval: 0,
          rotate: 30,
        },
        axisLine: { lineStyle: { color: '#374151' } },
      },
      yAxis: {
        type: 'value',
        name: '胜率(%)',
        nameTextStyle: {
          color: '#9ca3af',
          fontSize: 11,
        },
        minInterval: 1,
        max: 100,
        axisLabel: { 
          color: '#9ca3af',
          formatter: (value: number) => Math.round(value).toString(),
        },
        axisLine: { lineStyle: { color: '#374151' } },
        splitLine: { lineStyle: { color: '#374151', type: 'dashed' } },
      },
      series: [
        {
          name: '胜率',
          type: 'bar',
          data: roleStats.map(r => ({
            value: r.winRate,
            itemStyle: {
              color: r.faction === 'good' ? 'rgba(96, 165, 250, 0.8)' : 'rgba(248, 113, 113, 0.8)',
              borderColor: r.faction === 'good' ? '#60a5fa' : '#f87171',
              borderWidth: 1,
            },
          })),
          barWidth: '50%',
          label: {
            show: true,
            position: 'top',
            formatter: '{c}%',
            color: '#fff',
            fontSize: 11,
          },
        },
      ],
    };

    winRateChartInstance.current = chart;
    chart.setOption(option as any);

    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        if (winRateChartInstance.current) {
          winRateChartInstance.current.resize();
        }
      }, 100);
    };
    window.addEventListener('resize', handleResize);
    
    // V187: 初始渲染后强制resize确保正确尺寸
    setTimeout(() => {
      if (winRateChartInstance.current) {
        winRateChartInstance.current.resize();
      }
    }, 50);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (winRateChartInstance.current) {
        winRateChartInstance.current.dispose();
        winRateChartInstance.current = null;
      }
    };
  }, [roleStats]);

  const displayRoleStats = roleStats.length > 0 ? roleStats : STANDARD_ROLES.map(role => ({
    role,
    roleName: ROLE_NAMES[role],
    faction: ROLE_FACTION[role],
    games: 0,
    wins: 0,
    winRate: 0,
    avgScore: 0,
    pickRate: 0,
  }));

  return (
    <div className="space-y-4">
      {/* V188: 总场次统计卡片 - 压缩上下宽度 */}
      <div className="grid grid-cols-2 gap-2">
        <div className="avalon-card p-2">
          <div className="flex items-center gap-1.5 text-avalon-text text-xs mb-0.5">
            <Trophy className="w-3 h-3" />
            <span>总场次</span>
          </div>
          <div className="text-xl font-bold text-white">
            {playerStats.total_games || 0}
          </div>
        </div>
        <div className="avalon-card p-2">
          <div className="flex items-center gap-1.5 text-avalon-text text-xs mb-0.5">
            <Trophy className="w-3 h-3" />
            <span>角色种类</span>
          </div>
          <div className="text-xl font-bold text-white">
            {displayRoleStats.filter(r => r.games > 0).length}/8
          </div>
        </div>
      </div>

      {/* V187: Tab切换优化 - 修复卡顿问题 */}
      <div className="flex gap-2">
        <button
          onClick={() => {
            if (isTransitioning || activeView === 'pick') return;
            setIsTransitioning(true);
            setActiveView('pick');
            // V187: 切换后强制resize
            setTimeout(() => {
              if (pickRateChartInstance.current) {
                pickRateChartInstance.current.resize();
              }
              setIsTransitioning(false);
            }, 150);
          }}
          disabled={isTransitioning}
          className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm ${
            activeView === 'pick'
              ? 'bg-avalon-purple text-white'
              : 'bg-avalon-card text-avalon-text hover:text-white'
          } ${isTransitioning ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          <BarChart3 className="w-4 h-4" />
          <span>角色抓取率</span>
        </button>
        <button
          onClick={() => {
            if (isTransitioning || activeView === 'win') return;
            setIsTransitioning(true);
            setActiveView('win');
            // V187: 切换后强制resize
            setTimeout(() => {
              if (winRateChartInstance.current) {
                winRateChartInstance.current.resize();
              }
              setIsTransitioning(false);
            }, 150);
          }}
          disabled={isTransitioning}
          className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm ${
            activeView === 'win'
              ? 'bg-avalon-purple text-white'
              : 'bg-avalon-card text-avalon-text hover:text-white'
          } ${isTransitioning ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          <Radar className="w-4 h-4" />
          <span>角色胜率</span>
        </button>
      </div>

      {/* V187: 图表容器 - 使用绝对定位避免切换时的布局抖动，压缩高度 */}
      <div className="avalon-card p-2 relative" style={{ height: '280px' }}>
        <div
          ref={pickRateChartRef}
          className="w-full h-full absolute inset-0 p-2"
          style={{ 
            opacity: activeView === 'pick' ? 1 : 0,
            pointerEvents: activeView === 'pick' ? 'auto' : 'none',
            transition: 'opacity 0.2s ease-in-out',
            zIndex: activeView === 'pick' ? 10 : 0
          }}
        />
        <div
          ref={winRateChartRef}
          className="w-full h-full absolute inset-0 p-2"
          style={{ 
            opacity: activeView === 'win' ? 1 : 0,
            pointerEvents: activeView === 'win' ? 'auto' : 'none',
            transition: 'opacity 0.2s ease-in-out',
            zIndex: activeView === 'win' ? 10 : 0
          }}
        />
      </div>

      {/* V187: 角色抓取率下方增加阵营抓取率统计 - 压缩高度 */}
      {activeView === 'pick' && (
        <div className="avalon-card p-2">
          <h4 className="text-white font-medium mb-1.5 flex items-center gap-2 text-sm">
            <Users className="w-4 h-4 text-avalon-gold" />
            阵营抓取率
          </h4>
          <div className="grid grid-cols-2 gap-2">
            <div className="text-center p-1.5 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <div className="text-blue-400 text-xs mb-0.5">正义阵营</div>
              <div className="text-white font-bold text-base">
                {(playerStats.total_games || 0) > 0 ? `${factionPickRates.good}%` : '-'}
              </div>
            </div>
            <div className="text-center p-1.5 bg-red-500/10 rounded-lg border border-red-500/30">
              <div className="text-red-400 text-xs mb-0.5">邪恶阵营</div>
              <div className="text-white font-bold text-base">
                {(playerStats.total_games || 0) > 0 ? `${factionPickRates.evil}%` : '-'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* V187: 数据表格 - 压缩头部padding */}
      <div className="avalon-card overflow-hidden">
        <div className="p-2 border-b border-avalon-purple/20">
          <h4 className="text-white font-medium flex items-center gap-2 text-sm">
            <Trophy className="w-4 h-4 text-avalon-gold" />
            角色数据明细
          </h4>
        </div>
        <div className="w-full">
          <table className="w-full">
            <thead>
              <tr className="border-b border-avalon-purple/20">
                <th className="text-left py-2 px-3 text-avalon-text text-xs font-medium">角色</th>
                <th className="text-center py-2 px-3 text-avalon-text text-xs font-medium">阵营</th>
                <th className="text-center py-2 px-3 text-avalon-text text-xs font-medium">出场</th>
                <th className="text-center py-2 px-3 text-avalon-text text-xs font-medium">胜场</th>
                <th className="text-center py-2 px-3 text-avalon-text text-xs font-medium">胜率</th>
              </tr>
            </thead>
            <tbody>
              {displayRoleStats.map((stat, index) => (
                <tr
                  key={stat.role}
                  className={`border-b border-avalon-purple/10 last:border-0 ${
                    index % 2 === 0 ? 'bg-avalon-bg-dark/30' : ''
                  }`}
                >
                  <td className="py-2 px-3 text-white text-sm">{stat.roleName}</td>
                  <td className="py-2 px-3 text-center">
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        stat.faction === 'good'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {stat.faction === 'good' ? '正义' : '邪恶'}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-center text-avalon-text text-sm">{stat.games}</td>
                  <td className="py-2 px-3 text-center text-avalon-text text-sm">{stat.wins}</td>
                  <td className="py-2 px-3 text-center">
                    <span
                      className={`text-sm font-medium ${
                        stat.games === 0
                          ? 'text-avalon-text'
                          : stat.winRate >= 60
                          ? 'text-green-400'
                          : stat.winRate >= 40
                          ? 'text-avalon-gold'
                          : 'text-red-400'
                      }`}
                    >
                      {stat.games === 0 ? '0%' : `${stat.winRate}%`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 图例说明 */}
      <div className="flex items-center justify-center gap-4 text-xs text-avalon-text">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-500/60 border border-blue-500" />
          <span>正义阵营</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-red-500/60 border border-red-500" />
          <span>邪恶阵营</span>
        </div>
      </div>
    </div>
  );
}
